import { MenuItem } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { EntityEditBankInfoBlock } from "features/entities/edit/EntityEditBankInfoBlock";
import {
  EntityEditDrawer,
  EntityProfileSection,
} from "features/entities/edit/EntityEditDrawer";
import { EntityEditTabContext } from "features/entities/edit/EntityEditTabContext";
import { getFundRetrieveQueryKey } from "gen/endpoints/fund-entities/fund-entities";
import { useFundFamilyListSuspense } from "gen/endpoints/fund-family/fund-family";
import { getGlobalEntityRetrieveQueryKey } from "gen/endpoints/global-entity/global-entity";
import { EntityFunctionalTypeEnum } from "gen/model/entityFunctionalTypeEnum";
import { FundEntityReadData } from "gen/model/fundEntityReadData";
import { GlobalEntityData } from "gen/model/globalEntityData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { localizeJurisdiction } from "shared/utils/AddressUtils";

export const EntityEditTabDetails: React.FC = () => {
  const { t } = useTranslation(["enums", "common", "entity"]);
  const queryClient = useQueryClient();

  const { entity, fundFamilyId } = useOutletContext<EntityEditTabContext>();

  const { data: fundFamilies } = useFundFamilyListSuspense();

  const fundFamilyName = fundFamilies.find(
    (it) => it.id === entity.fund_family_id,
  )?.name;

  const [editingSection, setEditingSection] = useState<EntityProfileSection>();

  const onCloseComplete = useCallback(async () => {
    setEditingSection(undefined);
    await queryClient.invalidateQueries({
      queryKey: getGlobalEntityRetrieveQueryKey(entity.id!),
    });
    if (fundFamilyId) {
      await queryClient.invalidateQueries({
        queryKey: getFundRetrieveQueryKey(fundFamilyId, entity.id!),
      });
    }
  }, [entity.id, fundFamilyId, queryClient]);

  return (
    <PageTabBody>
      <ContentBlockGroup defaultIndex={[0, 1, 2]}>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockActions>
              <Restricted permission={PermissionTypeEnum.config}>
                <ContentBlockMenu>
                  <MenuItem
                    onClick={() => {
                      setEditingSection("basic");
                    }}
                  >
                    {t("common:edit")}
                  </MenuItem>
                </ContentBlockMenu>
              </Restricted>
            </ContentBlockActions>
          </ContentBlockHeader>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item label={t("entity:functionalType")}>
                {t(`enums:EntityFunctionalTypeEnum.${entity.functional_type!}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t("common:fundFamily")}>
                {fundFamilyName}
              </Descriptions.Item>
              <Descriptions.Item label={t("entity:jurisdiction")}>
                {localizeJurisdiction(t, entity)}
              </Descriptions.Item>
              <Descriptions.Item label={t("entity:legalType")}>
                {t(`enums:EntityLegalTypeEnum.${entity.legal_type}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t("entity:externalId")}>
                {entity.external_id}
              </Descriptions.Item>
              <Descriptions.Item label={t("entity:fullEntityName")}>
                {entity.legal_name}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>{t("common:tax.title")}</ContentBlockTitle>
            <ContentBlockActions>
              <Restricted permission={PermissionTypeEnum.config}>
                <ContentBlockMenu>
                  <MenuItem
                    onClick={() => {
                      setEditingSection("tax");
                    }}
                  >
                    {t("common:edit")}
                  </MenuItem>
                </ContentBlockMenu>
              </Restricted>
            </ContentBlockActions>
          </ContentBlockHeader>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item label={t("common:tax.type")}>
                {t(`common:tax.types.${entity.tax_id_type!}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t("common:tax.id")}>
                {entity.tax_id}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      {entityHasBankInfo(entity) && (
        <EntityEditBankInfoBlock entityId={entity.id!} />
      )}
      {editingSection && (
        <EntityEditDrawer
          entity={entity}
          onCloseComplete={onCloseComplete}
          section={editingSection}
        />
      )}
    </PageTabBody>
  );
};

const entityHasBankInfo = (entity: GlobalEntityData | FundEntityReadData) =>
  entity.functional_type === EntityFunctionalTypeEnum.main_fund ||
  entity.functional_type === EntityFunctionalTypeEnum.parallel_fund;
